export const indentString = (str: string, spaces: number): string => {
  const indent = " ".repeat(spaces);
  return str
    .split("\n")
    .map((line) => `${indent}${line}`)
    .join("\n");
};

// Test changes to this script by manually running the tests in __tests__/runCommand.test.ts
// If you modify the YAML structure here, please also update the corresponding
// Terraform SSM Document resource by running `yarn generate:ssm-yaml`.
export const SUDO_RUN_COMMAND_SCRIPT = (
  bashEscape: string,
  tfEscape: string
) => `#!/bin/bash
set -euo pipefail

ExitWithFailure() {
  MESSAGE="${bashEscape}\$1"
  (>&2 echo "${bashEscape}\$MESSAGE")
  exit 1
}

FindNextAvailableUID() {
  local MIN_UID=65536
  local MAX_UID=90000
  local NEW_UID=${bashEscape}\$MIN_UID

  # Check both UID and GID availability since we enforce UID == GID
  while id -u "${bashEscape}\$NEW_UID" &>/dev/null || getent group | cut -d: -f3 | grep -qx "${bashEscape}\$NEW_UID"; do
    ((NEW_UID++))
    if [ "${bashEscape}\$NEW_UID" -gt "${bashEscape}\$MAX_UID" ]; then
        echo "No available UID/GID found in range ${bashEscape}\$MIN_UID-${bashEscape}\$MAX_UID" >&2
        return 1
    fi
  done

  echo "${bashEscape}\$NEW_UID"
  return 0
}

DeleteGroup() {
  local USERNAME="${bashEscape}\$1"

  # Temporarily disable errexit to capture exit code
  set +e
  if [ -f /usr/sbin/groupdel ]; then
    timeout 30s groupdel "${bashEscape}\$USERNAME"
  elif [ -f /usr/sbin/delgroup ]; then
    timeout 30s delgroup "${bashEscape}\$USERNAME"
  else
    ExitWithFailure "Neither groupdel nor delgroup are available."
  fi
  local EXIT_CODE=${bashEscape}\$?
  set -e

  if [ ${bashEscape}\$EXIT_CODE -eq 124 ]; then
    ExitWithFailure "Timeout: Group deletion took longer than 30 seconds."
  elif [ ${bashEscape}\$EXIT_CODE -ne 0 ]; then
    ExitWithFailure "Failed to delete existing group."
  fi
}

EnsureGroup() {
  local USERNAME="${bashEscape}\$1"
  local DESIRED_GID="${bashEscape}\$2"

  # Check if group already exists
  local EXISTING_GID=${bashEscape}\$(getent group "${bashEscape}\$USERNAME" | cut -d: -f3)
  if [ -n "${bashEscape}\$EXISTING_GID" ]; then
    if [ "${bashEscape}\$EXISTING_GID" = "${bashEscape}\$DESIRED_GID" ]; then
      # Group exists with correct GID - continue
      return 0
    fi
    # Group exists with wrong GID - delete and recreate (will error if in use)
    DeleteGroup "${bashEscape}\$USERNAME"
  fi

  # Temporarily disable errexit to capture exit code
  set +e
  if [ -f /usr/sbin/groupadd ]; then
    timeout 30s /usr/sbin/groupadd -g "${bashEscape}\$DESIRED_GID" "${bashEscape}\$USERNAME"
  elif [ -f /usr/sbin/addgroup ]; then
    timeout 30s /usr/sbin/addgroup --gid "${bashEscape}\$DESIRED_GID" "${bashEscape}\$USERNAME"
  else
    ExitWithFailure "Neither groupadd nor addgroup are available."
  fi
  local EXIT_CODE=${bashEscape}\$?
  set -e

  if [ ${bashEscape}\$EXIT_CODE -eq 124 ]; then
    ExitWithFailure "Timeout: Group creation took longer than 30 seconds."
  elif [ ${bashEscape}\$EXIT_CODE -ne 0 ]; then
    ExitWithFailure "Failed to create the specified group."
  fi
}

CreateUser() {
  local USERNAME="${bashEscape}\$1"
  local USER_UID="${bashEscape}\$2"

  # Temporarily disable errexit to capture exit code
  set +e
  if [ -f /usr/sbin/useradd ]; then
    timeout 30s /usr/sbin/useradd -m -u "${bashEscape}\$USER_UID" -g "${bashEscape}\$USER_UID" -s /bin/bash "${bashEscape}\$USERNAME"
  elif [ -f /usr/sbin/adduser ]; then
    timeout 30s /usr/sbin/adduser --uid "${bashEscape}\$USER_UID" --gid "${bashEscape}\$USER_UID" --gecos "${bashEscape}\$USERNAME" --disabled-password --shell /bin/bash "${bashEscape}\$USERNAME"
  else
    ExitWithFailure "Neither useradd nor adduser are available."
  fi
  local EXIT_CODE=${bashEscape}\$?
  set -e

  if [ ${bashEscape}\$EXIT_CODE -eq 124 ]; then
    ExitWithFailure "Timeout: User creation took longer than 30 seconds."
  elif [ ${bashEscape}\$EXIT_CODE -ne 0 ]; then
    ExitWithFailure "Failed to create the specified user."
  fi
}

EnsureUserExists() {
  local USERNAME="${bashEscape}\$1"

  if id "${bashEscape}\$USERNAME" &>/dev/null; then
      return 0
  fi

  local NEW_UID=${bashEscape}\$(FindNextAvailableUID) || ExitWithFailure "No available UID/GID found."

  EnsureGroup "${bashEscape}\$USERNAME" "${bashEscape}\$NEW_UID"
  CreateUser "${bashEscape}\$USERNAME" "${bashEscape}\$NEW_UID"
}

EnsureLineInFile() {
  local LINE="${bashEscape}\$1"
  local FILE="${bashEscape}\$2"

  if ! grep -qF "${bashEscape}\$LINE" "${bashEscape}\$FILE"; then
      echo "${bashEscape}\$LINE" | tee -a "${bashEscape}\$FILE" >/dev/null
  fi
}

EnsureFileExists() {
  local FILE_PATH="${bashEscape}\$1"
  mkdir -p "${bashEscape}\$(dirname "${bashEscape}\$FILE_PATH")"
  if [ ! -e "${bashEscape}\$FILE_PATH" ]; then
    touch "${bashEscape}\$FILE_PATH"
  fi
}

UpdateFile() {
  local REQUEST_ID="${bashEscape}\$1"
  local FILE_PATH="${bashEscape}\$2"
  local ACTION="${bashEscape}\$3"
  local CONTENT="${bashEscape}\$4"
  local PERMISSION="${bashEscape}\$5"

  if [ ! -f "${bashEscape}\$FILE_PATH" ]; then
    return 0
  fi

  local NOW=${bashEscape}\$(date +%s)
  local TEMP_FILE="${tfEscape}${bashEscape}\${FILE_PATH}.tmp.${bashEscape}\$${bashEscape}\$"
  local MAX_REVOKE_AGE=604800  # 7 days in seconds

  # Create empty temp file
  : > "${bashEscape}\$TEMP_FILE"

  # Flag to track whether to skip adding new grant/revoke line
  local do_not_add=0
  # Flag to keep the next line (used for actual "grant" content)
  local keep_next_line=0
  # Flag to skip the next line (used for revoking)
  local skip_next_line=0

  # Process file line-by-line. Only clean up P0-generated content if action is "cleanup":
  # - Comments starting with "# RequestID: ... | ..."
  # - For authorized_keys: orphaned public keys ending with "p0-generated-key"
  # All other content is preserved.
  while IFS= read -r line || [ -n "${bashEscape}\$line" ]; do
    if [ ${bashEscape}\$skip_next_line -eq 1 ]; then
      # Content line following a revoked GrantedAt - always skip
      skip_next_line=0
    elif [ ${bashEscape}\$keep_next_line -eq 1 ]; then
      # Content line following a kept P0 GrantedAt - always keep
      echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
      keep_next_line=0
    elif [[ "${bashEscape}\$line" =~ ^#[[:space:]]RequestID:[[:space:]](.+)[[:space:]]\\|[[:space:]](GrantedAt|RevokedAt):[[:space:]]([0-9]+) ]]; then
      # Properly formatted P0 comment line matching: "# RequestID: <id> | <GrantedAt|RevokedAt>: <timestamp>"
      # Captures: [1]=request_id, [2]=type (GrantedAt/RevokedAt), [3]=timestamp
      local line_request_id="${tfEscape}${bashEscape}\${BASH_REMATCH[1]}"
      local line_type="${tfEscape}${bashEscape}\${BASH_REMATCH[2]}"
      local line_timestamp="${tfEscape}${bashEscape}\${BASH_REMATCH[3]}"

      if [ "${bashEscape}\$line_request_id" = "${bashEscape}\$REQUEST_ID" ]; then
        # Current RequestID
        if [ "${bashEscape}\$line_type" = "RevokedAt" ]; then
          echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
          # If the ACTION is "revoke", an identical revoke for this request ID already exists (idempotency)
          # If the ACTION is "grant", a revoke tombstone for this request ID exists (out-of-order protection)
          do_not_add=1
        else # line_type can only be "GrantedAt"
          if [ "${bashEscape}\$ACTION" = "grant" ]; then
            echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
            # A "grant" action should not be added an identical grant for this request ID already exists (idempotency)
            do_not_add=1
            keep_next_line=1
          else # action can only be "revoke" because "cleanup" does not specify a RequestID
            # For "revoke" action, remove this GrantedAt line and its content line
            skip_next_line=1
          fi
        fi
      else
        # Different RequestID - always clean up old RevokedAt tombstones
        if [ "${bashEscape}\$line_type" = "RevokedAt" ]; then
          local age=${bashEscape}\$((NOW - line_timestamp))
          if [ ${bashEscape}\$age -lt ${bashEscape}\$MAX_REVOKE_AGE ]; then
            echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
          fi
        else # line_type can only be "GrantedAt"
          echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
          keep_next_line=1
        fi
      fi
    else
      # Non-properly formatted line - only clean up content if action is "cleanup"
      if [ "${bashEscape}\$ACTION" = "cleanup" ]; then
        # Cleanup action: only malformed P0-owned lines are removed
        if [[ "${bashEscape}\$line" =~ ^#[[:space:]]RequestID: ]]; then
          # Partial P0 comment without proper format - skip (malformed)
          :
        elif [[ "${bashEscape}\$FILE_PATH" == *"authorized_keys"* ]]; then
          # For authorized_keys, check if it's an orphaned P0-generated key
          if [[ "${bashEscape}\$line" =~ p0-generated-key[[:space:]]*${bashEscape}\$ ]]; then
            # Orphaned P0 key not following a kept GrantedAt - skip (malformed)
            :
          else
            # Not a P0 key - keep it (user content)
            echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
          fi
        fi
        # For sudoers-p0, skip everything else (comments, empty lines, orphaned entries)
      else
        # Grant or Revoke action: preserve all existing content (including malformed lines)
        echo "${bashEscape}\$line" >> "${bashEscape}\$TEMP_FILE"
      fi
    fi
  done < "${bashEscape}\$FILE_PATH"

  # After line-by-line processing, add new grant entry / revoke tombstone if needed
  if [ ${bashEscape}\$do_not_add -eq 0 ]; then
    if [ "${bashEscape}\$ACTION" = "revoke" ]; then
      echo "# RequestID: ${bashEscape}\$REQUEST_ID | RevokedAt: ${bashEscape}\$NOW" >> "${bashEscape}\$TEMP_FILE"
    elif [ "${bashEscape}\$ACTION" = "grant" ]; then
      echo "# RequestID: ${bashEscape}\$REQUEST_ID | GrantedAt: ${bashEscape}\$NOW" >> "${bashEscape}\$TEMP_FILE"
      echo "${bashEscape}\$CONTENT" >> "${bashEscape}\$TEMP_FILE"
    fi
  fi

  mv "${bashEscape}\$TEMP_FILE" "${bashEscape}\$FILE_PATH"
  # Always ensure correct permissions
  chmod "${bashEscape}\$PERMISSION" "${bashEscape}\$FILE_PATH"
}

# Create secure lock directory with restrictive permissions
mkdir -p /run/p0
chown root:root /run/p0
chmod 700 /run/p0

# Use flock to serialize all operations (user creation, sudoers, SSH keys)
# Protects shared resources like /etc/sudoers-p0 from concurrent writes
(
  flock -x -w 30 200 || ExitWithFailure "Failed to acquire lock after 30 seconds."

  # Ensure user exists and files are set up
  EnsureUserExists '{{ UserName }}'
  EnsureFileExists "/etc/sudoers-p0"
  EnsureFileExists '/home/{{ UserName }}/.ssh/authorized_keys'

  if [ '{{ Action }}' = "grant" ]; then
    if [ -n '{{ Sudo }}' ] && [ '{{ Sudo }}' = "true" ]; then
      UpdateFile '{{ RequestId }}' "/etc/sudoers-p0" "grant" '{{ UserName }} ALL=(ALL) NOPASSWD: ALL' "440"
      EnsureLineInFile "#include sudoers-p0" /etc/sudoers
    fi
    if [ -n '{{ PublicKey }}' ] && [ '{{ PublicKey }}' != "N/A" ]; then
      UpdateFile '{{ RequestId }}' '/home/{{ UserName }}/.ssh/authorized_keys' "grant" '{{ PublicKey }}' "600"
      chown -R '{{ UserName }}:{{ UserName }}' '/home/{{ UserName }}/.ssh'
    fi
  elif [ '{{ Action }}' = "revoke" ]; then
    UpdateFile '{{ RequestId }}' "/etc/sudoers-p0" "revoke" "" "440"
    UpdateFile '{{ RequestId }}' '/home/{{ UserName }}/.ssh/authorized_keys' "revoke" "" "600"
  else # Action is "cleanup"
    # PublicKey, RequestID, Sudo parameters are ignored for cleanup
    # Always clean up sudoers-p0 file and pass empty PublicKey and RequestID
    UpdateFile "" "/etc/sudoers-p0" "cleanup" "" "440"
    UpdateFile "" '/home/{{ UserName }}/.ssh/authorized_keys' "cleanup" "" "600"
  fi

  echo "Completed"
  exit 0
) 200>/run/p0/p0-operations.lock
`;
